import React, { useState, useMemo } from "react";
import QueueSaveControls from "./QueueSaveControls";
import './TaskQueueFilter.css';

const TaskQueueFilter = ({ availableQueues, selectedTaskqueues, onChange, userId, onClose }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const isMobile = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(max-width: 900px)').matches;

  const term = (searchTerm || '').toLowerCase();
  const selectedSids = useMemo(
    () => (Array.isArray(selectedTaskqueues) ? selectedTaskqueues : []),
    [selectedTaskqueues]
  );

  // Map available queues for lookup by sid
  const availBySid = useMemo(() => {
    const map = new Map();
    (availableQueues || []).forEach(q => {
      const sid = (q.sid || '').toString();
      const label = (q.friendlyName || q.sid || '').toString();
      if (sid) map.set(sid, { sid, label });
    });
    return map;
  }, [availableQueues]);

  // Selected list from Firebase SIDs; label from available if present
  const selectedFiltered = useMemo(() => {
    return selectedSids
      .map(sid => {
        const hit = availBySid.get((sid || '').toString());
        const display = hit?.label || sid;
        return { sid: sid.toString(), display: display.toString() };
      })
      .filter(item => item.display.toLowerCase().includes(term));
  }, [selectedSids, availBySid, term]);

  // Available list: all queues from Twilio minus selected names
  const notSelectedFiltered = useMemo(() => {
    const selectedSet = new Set(selectedSids.map(n => n.toString()));
    return (availableQueues || [])
      .filter(q => !selectedSet.has((q.sid || '').toString()))
      .map(q => ({ sid: (q.sid || '').toString(), display: (q.friendlyName || q.sid || '').toString() }))
      .filter(item => item.display.toLowerCase().includes(term));
  }, [availableQueues, selectedSids, term]);

  // Toggle a single queue by friendlyName
  const toggleTaskqueue = (sid) => {
    if (!onChange) return;

    if (selectedTaskqueues.includes(sid)) {
      onChange(selectedTaskqueues.filter(id => id !== sid));
    } else {
      onChange([...(selectedTaskqueues || []), sid]);
    }
  };

  const handleSaved = (msg) => {
    setSuccessMsg(msg || 'Opgeslagen');
    setTimeout(() => setSuccessMsg(""), 3000);
  };

  // Removed bulk actions (select all filtered / clear all) per request
  const selectAllVisible = () => {
    // Select all currently visible items in "Beschikbare Queues" (respects search)
    const sids = notSelectedFiltered.map(q => q.sid).filter(Boolean);
    const merged = Array.from(new Set([...(selectedTaskqueues || []), ...sids]));
    onChange?.(merged);
  };

  const containerStyle = isMobile
    ? { position:'relative', width:'100%', height:'100%', display:'flex', flexDirection:'column' }
    : { maxWidth: 860, margin:'0 auto', position:'relative' };

  return (
    <div className="tq-filter" style={containerStyle}>
      {onClose && !isMobile && (
        <button onClick={onClose} aria-label="Sluiten" className="tq-filter__btn outline" style={{ position:'absolute', top:0, right:0 }}>
          sluit
        </button>
      )}
      <div className="tq-filter__header">
        <div className="tq-filter__title">Queues filter</div>
        <input
          className="tq-filter__search"
          placeholder="Zoek naar servicegroepen/queues..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
        />
        <div className="tq-filter__actions">
          <button type="button" className="tq-filter__btn secondary" onClick={selectAllVisible}>Selecteer alle</button>
          {!isMobile && (
            <QueueSaveControls userId={userId} selectedSids={selectedSids} onSaved={handleSaved} className="tq-filter__btn" />
          )}
        </div>
      </div>
  <div className="tq-filter__lists" style={isMobile ? { flex:1, overflow:'auto' } : undefined}>
        <div className="tq-filter__panel">
          <div className="tq-filter__panelTitle">Beschikbare Queues ({notSelectedFiltered.length})</div>
          {notSelectedFiltered.length ? (
            <ul className="tq-filter__list">
              {notSelectedFiltered.map(({ sid, display }) => (
                <li key={sid} className="tq-filter__item" onClick={() => toggleTaskqueue(sid)}>
                  <input type="checkbox" checked={false} readOnly />
                  {display}
                </li>
              ))}
            </ul>
          ) : (<div className="tq-filter__empty">Geen beschikbare queues</div>)}
        </div>
        <div className="tq-filter__panel">
          <div className="tq-filter__panelTitle">Geselecteerde Queues ({selectedFiltered.length})</div>
          {selectedFiltered.length ? (
            <ul className="tq-filter__list">
              {selectedFiltered.map(({ sid, display }) => (
                <li key={sid} className="tq-filter__item" onClick={() => toggleTaskqueue(sid)}>
                  <input type="checkbox" checked readOnly />
                  {display}
                </li>
              ))}
            </ul>
          ) : (<div className="tq-filter__empty">Geen geselecteerde queues</div>)}
        </div>
      </div>
      {successMsg && (
        <div style={{ marginTop:'0.75rem', padding:'.6rem', background: successMsg.includes('Error') ? '#e74c3c' : '#2ecc71', color:'#fff', borderRadius:'.5rem', fontWeight:600, textAlign:'center', fontSize:'.85rem' }}>{successMsg}</div>
      )}
      {isMobile && (
        <div className="tq-filter__bottomBar">
          <button type="button" className="tq-filter__btn outline" onClick={onClose}>Sluiten</button>
          <QueueSaveControls userId={userId} selectedSids={selectedSids} onSaved={handleSaved} className="tq-filter__btn" />
        </div>
      )}
    </div>
  );
};

export default TaskQueueFilter;
